/**
 * Can Component
 * Conditionally render children based on permissions
 */

import { FC, ReactNode } from 'react';
import { useCan, CanContext } from '../hooks/useCan';
import { Action, Subject } from '../types';

interface CanProps {
  I: Action;
  a: Subject;
  children: ReactNode;
  fallback?: ReactNode;
  passThrough?: boolean;
  context?: CanContext;
}

/**
 * Can component for declarative permission checking
 *
 * @example
 * <Can I="create" a="Course">
 *   <CreateCourseButton />
 * </Can>
 *
 * @example with fallback
 * <Can I="access" a="AdminPanel" fallback={<AccessDenied />}>
 *   <AdminPanel />
 * </Can>
 *
 * @example with context
 * <Can I="start" a="Course" context={{ readOnly: true }}>
 *   <StartCourseButton />
 * </Can>
 */
export const Can: FC<CanProps> = ({
  I,
  a,
  children,
  fallback = null,
  passThrough = false,
  context,
}) => {
  const can = useCan(I, a, context);

  if (!can && passThrough) {
    return <>{children}</>;
  }

  return can ? <>{children}</> : <>{fallback}</>;
};
